import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNavigation } from '../components/BottomNavigation';
import { BackButton } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { 
  createDiscount, 
  getDiscountsBySeller, 
  updateDiscount, 
  deleteDiscount, 
  generateRandomDiscountCode,
  getDiscountStats
} from '../utils/discountApi';
import { getProductsBySeller } from '../utils/productApi';
import { toast } from 'react-hot-toast';
import { 
  Plus, 
  Edit, 
  Delete, 
  Search, 
  Filter,
  TickSquare,
  CloseSquare,
  TimeCircle,
  Package,
  Tag
} from 'react-iconly';
import { COLORS } from '../lib/styleGuide';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};

const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

const Discounts = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State
  const [discounts, setDiscounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    amount: '',
    minSubtotal: '',
    scope: 'entire_order',
    productIds: [],
    categoryIds: [],
    status: 'active'
  });

  // Load data on component mount
  useEffect(() => {
    if (currentUser?.uid) {
      loadData();
    }
  }, [currentUser?.uid]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [discountsData, productsData, statsData] = await Promise.all([
        getDiscountsBySeller(currentUser.uid),
        getProductsBySeller(currentUser.uid),
        getDiscountStats(currentUser.uid)
      ]);
      
      setDiscounts(discountsData);
      setProducts(productsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load discount data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRandomizeCode = () => {
    const randomCode = generateRandomDiscountCode(8);
    setFormData(prev => ({
      ...prev,
      code: randomCode
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const discountData = {
        ...formData,
        sellerId: currentUser.uid,
        code: formData.code.toUpperCase(),
        amount: parseFloat(formData.amount),
        minSubtotal: formData.minSubtotal ? parseFloat(formData.minSubtotal) : null
      };

      if (editingDiscount) {
        await updateDiscount(editingDiscount.id, discountData);
        toast.success('Discount updated successfully!');
      } else {
        await createDiscount(discountData);
        toast.success('Discount created successfully!');
      }
      
      // Reset form and reload data
      resetForm();
      await loadData();
      
    } catch (error) {
      console.error('Error saving discount:', error);
      toast.error(error.message || 'Failed to save discount');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (discount) => {
    setEditingDiscount(discount);
    setFormData({
      code: discount.code,
      type: discount.type,
      amount: discount.amount.toString(),
      minSubtotal: discount.minSubtotal ? discount.minSubtotal.toString() : '',
      scope: discount.scope,
      productIds: discount.productIds || [],
      categoryIds: discount.categoryIds || [],
      status: discount.status
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (discountId) => {
    if (window.confirm('Are you sure you want to delete this discount code?')) {
      try {
        await deleteDiscount(discountId);
        toast.success('Discount deleted successfully!');
        await loadData();
      } catch (error) {
        console.error('Error deleting discount:', error);
        toast.error('Failed to delete discount');
      }
    }
  };

  const handleToggleStatus = async (discount) => {
    const newStatus = discount.status === 'active' ? 'inactive' : 'active';
    try {
      await updateDiscount(discount.id, { status: newStatus });
      toast.success(`Discount ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
      await loadData();
    } catch (error) {
      console.error('Error updating discount status:', error);
      toast.error('Failed to update discount status');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      type: 'percentage',
      amount: '',
      minSubtotal: '',
      scope: 'entire_order',
      productIds: [],
      categoryIds: [],
      status: 'active'
    });
    setEditingDiscount(null);
    setShowCreateForm(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: SUCCESS_GREEN, icon: TickSquare, label: 'Active' },
      inactive: { color: ACCENT_GRAY, icon: CloseSquare, label: 'Inactive' },
      expired: { color: ERROR_RED, icon: TimeCircle, label: 'Expired' }
    };
    
    const config = statusConfig[status] || statusConfig.inactive;
    const IconComponent = config.icon;
    
    return (
      <span style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 8px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        color: config.color,
        background: config.color + '15'
      }}>
        <IconComponent size={12} primaryColor={config.color} />
        {config.label}
      </span>
    );
  };

  const getScopeLabel = (scope) => {
    const scopeLabels = {
      entire_order: 'Entire Order',
      specific_products: 'Specific Products',
      specific_categories: 'Specific Categories'
    };
    return scopeLabels[scope] || scope;
  };

  const getTypeIcon = (type) => {
    return type === 'percentage' ? 
      <span style={{ fontSize: 16, color: PRIMARY_BROWN, fontWeight: 600 }}>%</span> : 
      <span style={{ fontSize: 16, color: PRIMARY_BROWN, fontWeight: 600 }}>$</span>;
  };

  // Filter discounts based on search and status
  const filteredDiscounts = discounts.filter(discount => {
    const matchesSearch = discount.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || discount.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: NEUTRAL_BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, color: NEUTRAL_TEXT, marginBottom: 16 }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_BG, paddingBottom: 96 }}>
      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 10, background: NEUTRAL_WHITE, borderBottom: '1px solid #eee', padding: '12px 0 0 0', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 20px 12px 20px' }}>
          <BackButton
            onClick={() => navigate(-1)}
            variant="standard"
            ariaLabel="Back"
          />
          <h1 style={{ ...headingFont, fontSize: 22, color: NEUTRAL_TEXT, margin: 0 }}>Discount Codes</h1>
        </div>
      </header>

      {/* Stats Cards */}
      <div style={{ padding: '0 20px 20px 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 16, marginBottom: 24 }}>
          <div style={{ background: NEUTRAL_WHITE, padding: 16, borderRadius: 12, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: PRIMARY_BROWN, marginBottom: 4 }}>{stats.total || 0}</div>
            <div style={{ fontSize: 12, color: ACCENT_GRAY }}>Total Codes</div>
          </div>
          <div style={{ background: NEUTRAL_WHITE, padding: 16, borderRadius: 12, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: SUCCESS_GREEN, marginBottom: 4 }}>{stats.active || 0}</div>
            <div style={{ fontSize: 12, color: ACCENT_GRAY }}>Active</div>
          </div>
          <div style={{ background: NEUTRAL_WHITE, padding: 16, borderRadius: 12, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: ACCENT_GRAY, marginBottom: 4 }}>{stats.totalUsage || 0}</div>
            <div style={{ fontSize: 12, color: ACCENT_GRAY }}>Total Uses</div>
          </div>
          <div style={{ background: NEUTRAL_WHITE, padding: 16, borderRadius: 12, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: SUCCESS_GREEN, marginBottom: 4 }}>${(stats.totalSavings || 0).toFixed(2)}</div>
            <div style={{ fontSize: 12, color: ACCENT_GRAY }}>Total Savings</div>
          </div>
        </div>

        {/* Search and Filter */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={16} primaryColor={ACCENT_GRAY} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                border: '1px solid #E0E0E0',
                borderRadius: 8,
                fontSize: 14,
                ...bodyFont
              }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '1px solid #E0E0E0',
              borderRadius: 8,
              fontSize: 14,
              background: NEUTRAL_WHITE,
              cursor: 'pointer',
              ...bodyFont
            }}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="expired">Expired</option>
          </select>
        </div>

        {/* Create Discount Button */}
        <button
          onClick={() => setShowCreateForm(true)}
          style={{
            background: COLORS.PRIMARY_GRADIENT,
            color: NEUTRAL_WHITE,
            border: 'none',
            borderRadius: 12,
            padding: '14px 24px',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 20,
            ...bodyFont
          }}
        >
          <Plus size={20} primaryColor={NEUTRAL_WHITE} />
          Create Discount
        </button>

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div style={{ background: NEUTRAL_WHITE, borderRadius: 16, padding: 24, marginBottom: 24, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
            <h2 style={{ ...headingFont, fontSize: 20, color: NEUTRAL_TEXT, marginBottom: 20 }}>
              {editingDiscount ? 'Edit Discount' : 'Create a Discount'}
            </h2>
            
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Code Field */}
              <div>
                <label style={{ display: 'block', fontSize: 14, color: ACCENT_GRAY, fontWeight: 600, marginBottom: 8 }}>Code</label>
                <div style={{ display: 'flex', gap: 12 }}>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value)}
                    placeholder="E.G., SUMMER20"
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: '1px solid #E0E0E0',
                      borderRadius: 8,
                      fontSize: 14,
                      ...bodyFont
                    }}
                    required
                  />
                  <button
                    type="button"
                    onClick={handleRandomizeCode}
                    style={{
                      padding: '12px 16px',
                      background: NEUTRAL_BG,
                      border: '1px solid #E0E0E0',
                      borderRadius: 8,
                      fontSize: 14,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      ...bodyFont
                    }}
                  >
                    Randomize
                  </button>
                </div>
              </div>

              {/* Discount Type */}
              <div>
                <label style={{ display: 'block', fontSize: 14, color: ACCENT_GRAY, fontWeight: 600, marginBottom: 8 }}>Discount Type</label>
                <div style={{ display: 'flex', gap: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="type"
                      value="percentage"
                      checked={formData.type === 'percentage'}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                    />
                    <span style={{ fontSize: 14, ...bodyFont }}>% off</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="type"
                      value="fixed"
                      checked={formData.type === 'fixed'}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                    />
                    <span style={{ fontSize: 14, ...bodyFont }}>$ off</span>
                  </label>
                </div>
              </div>

              {/* Amount */}
              <div>
                <label style={{ display: 'block', fontSize: 14, color: ACCENT_GRAY, fontWeight: 600, marginBottom: 8 }}>Amount</label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  placeholder={formData.type === 'percentage' ? 'e.g., 20' : 'e.g., 5.00'}
                  min={formData.type === 'percentage' ? 1 : 0.01}
                  max={formData.type === 'percentage' ? 100 : undefined}
                  step={formData.type === 'percentage' ? 1 : 0.01}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    fontSize: 14,
                    ...bodyFont
                  }}
                  required
                />
                <p style={{ fontSize: 12, color: ACCENT_GRAY, marginTop: 4, marginBottom: 0 }}>
                  For % off, enter 1-100. For $ off, enter a positive amount.
                </p>
              </div>

              {/* Minimum Order Subtotal */}
              <div>
                <label style={{ display: 'block', fontSize: 14, color: ACCENT_GRAY, fontWeight: 600, marginBottom: 8 }}>
                  Minimum order subtotal (optional)
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: ACCENT_GRAY }}>$</span>
                  <input
                    type="number"
                    value={formData.minSubtotal}
                    onChange={(e) => handleInputChange('minSubtotal', e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '12px 12px 12px 32px',
                      border: '1px solid #E0E0E0',
                      borderRadius: 8,
                      fontSize: 14,
                      ...bodyFont
                    }}
                  />
                </div>
              </div>

              {/* Scope */}
              <div>
                <label style={{ display: 'block', fontSize: 14, color: ACCENT_GRAY, fontWeight: 600, marginBottom: 8 }}>Scope</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="scope"
                      value="entire_order"
                      checked={formData.scope === 'entire_order'}
                      onChange={(e) => handleInputChange('scope', e.target.value)}
                    />
                    <span style={{ fontSize: 14, ...bodyFont }}>Entire order</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="scope"
                      value="specific_products"
                      checked={formData.scope === 'specific_products'}
                      onChange={(e) => handleInputChange('scope', e.target.value)}
                    />
                    <span style={{ fontSize: 14, ...bodyFont }}>Specific products</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="scope"
                      value="specific_categories"
                      checked={formData.scope === 'specific_categories'}
                      onChange={(e) => handleInputChange('scope', e.target.value)}
                    />
                    <span style={{ fontSize: 14, ...bodyFont }}>Specific categories</span>
                  </label>
                </div>
              </div>

              {/* Form Actions */}
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    flex: 1,
                    background: COLORS.PRIMARY_GRADIENT,
                    color: NEUTRAL_WHITE,
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 24px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.6 : 1,
                    ...bodyFont
                  }}
                >
                  {saving ? 'Saving...' : (editingDiscount ? 'Update Discount' : 'Create Discount')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '12px 24px',
                    background: NEUTRAL_BG,
                    border: '1px solid #E0E0E0',
                    borderRadius: 8,
                    fontSize: 14,
                    cursor: 'pointer',
                    ...bodyFont
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Discounts List */}
        <div style={{ background: NEUTRAL_WHITE, borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          {filteredDiscounts.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 18, color: ACCENT_GRAY, marginBottom: 8 }}>
                {discounts.length === 0 ? 'No discount codes yet' : 'No discounts match your search'}
              </div>
              <div style={{ fontSize: 14, color: ACCENT_GRAY }}>
                {discounts.length === 0 ? 'Create your first discount code to get started!' : 'Try adjusting your search or filters'}
              </div>
            </div>
          ) : (
            <div>
              {filteredDiscounts.map((discount) => (
                <div
                  key={discount.id}
                  style={{
                    padding: 20,
                    borderBottom: '1px solid #F0F0F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16
                  }}
                >
                  {/* Discount Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: NEUTRAL_TEXT }}>
                        {discount.code}
                      </div>
                      {getStatusBadge(discount.status)}
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      {getTypeIcon(discount.type)}
                      <span style={{ fontSize: 14, color: NEUTRAL_TEXT }}>
                        {discount.type === 'percentage' ? `${discount.amount}% off` : `$${discount.amount} off`}
                      </span>
                    </div>
                    
                    <div style={{ fontSize: 12, color: ACCENT_GRAY }}>
                      {getScopeLabel(discount.scope)}
                      {discount.minSubtotal && ` • Min. $${discount.minSubtotal}`}
                    </div>
                    
                    <div style={{ fontSize: 12, color: ACCENT_GRAY, marginTop: 4 }}>
                      Used {discount.usageCount || 0} times
                      {discount.totalDiscountAmount > 0 && ` • $${discount.totalDiscountAmount.toFixed(2)} total savings`}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleToggleStatus(discount)}
                      style={{
                        padding: '8px 12px',
                        background: discount.status === 'active' ? WARNING_ORANGE : SUCCESS_GREEN,
                        color: NEUTRAL_WHITE,
                        border: 'none',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        ...bodyFont
                      }}
                    >
                      {discount.status === 'active' ? 'Deactivate' : 'Activate'}
                    </button>
                    
                    <button
                      onClick={() => handleEdit(discount)}
                      style={{
                        padding: '8px 12px',
                        background: NEUTRAL_BG,
                        border: '1px solid #E0E0E0',
                        borderRadius: 6,
                        fontSize: 12,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      <Edit size={14} primaryColor={ACCENT_GRAY} />
                      Edit
                    </button>
                    
                    <button
                      onClick={() => handleDelete(discount.id)}
                      style={{
                        padding: '8px 12px',
                        background: ERROR_RED,
                        color: NEUTRAL_WHITE,
                        border: 'none',
                        borderRadius: 6,
                        fontSize: 12,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      <Delete size={14} primaryColor={NEUTRAL_WHITE} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default Discounts;
